import React, { useState, createContext, useContext, useEffect } from "react";
import { useApiService } from "../shared/apiservice";
import { Product, ProductStock } from "../shared/types";

export type SortOrder = "asc" | "desc";

const productsContext = createContext<{
    products: Product[];
    errors: string[];
    loading: boolean;
    page: number;
    perPage: number;
    total: number;
    searchTerm: string;
    includeInactive: boolean;
    supplierId: number | null;
    sortField: string;
    sortOrder: SortOrder;
    fetchProducts: () => void;
    setPage: (page: number) => void;
    setPerPage: (perPage: number) => void;
    setSearchTerm: (term: string) => void;
    setIncludeInactive: (include: boolean) => void;
    setSupplierId: (id: number | null) => void;
    setSort: (field: string) => void;
}>({
    products: [],
    errors: [],
    loading: false,
    page: 1,
    perPage: 15,
    total: 0,
    searchTerm: "",
    includeInactive: false,
    supplierId: null,
    sortField: "code",
    sortOrder: "asc",
    fetchProducts: () => { },
    setPage: () => { },
    setPerPage: () => { },
    setSearchTerm: () => { },
    setIncludeInactive: () => { },
    setSupplierId: () => { },
    setSort: () => { },
});

function parseProduct(d: any): Product {
    return {
        id: d.id,
        barcode: d.barcode,
        code: d.code,
        main_code: d.main_code,
        status: d.status,
        name: d.name,
        size: d.size,
        pack: d.pack,
        weight: d.weight,
        active: d.active,
        default_expiry: d.default_expiry,
    };
}

function useProviderProducts() {
    const [products, setProducts] = useState<Product[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPageInternal] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTermInternal] = useState("");
    const [includeInactive, setIncludeInactiveInternal] = useState(false);
    const [supplierId, setSupplierIdInternal] = useState<number | null>(null);
    const [sortField, setSortField] = useState("code");
    const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
    const apiService = useApiService();

    function setSearchTerm(term: string) {
        setSearchTermInternal(term);
        setPageInternal(1);
    }

    function setIncludeInactive(include: boolean) {
        setIncludeInactiveInternal(include);
        setPageInternal(1);
    }

    function setSupplierId(id: number | null) {
        setSupplierIdInternal(id);
        setPageInternal(1);
    }

    function setPage(p: number) {
        setPageInternal(p);
    }

    function setSort(field: string) {
        if (field === sortField) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
        setPageInternal(1);
    }

    useEffect(() => {
        fetchProducts();
    }, [page, perPage, searchTerm, includeInactive, supplierId, sortField, sortOrder]);

    async function fetchProducts() {
        setLoading(true);
        setErrors([]);

        try {
            const params = new URLSearchParams({
                page: String(page),
                per_page: String(perPage),
                sort: sortField,
                order: sortOrder,
            });
            if (searchTerm.trim()) {
                params.set("q", searchTerm.trim());
            }
            if (includeInactive) {
                params.set("includeInactive", "1");
            }
            if (supplierId != null) {
                params.set("supplier", String(supplierId));
            }
            const response = await apiService.get(
                `/products?${params.toString()}`
            );
            if (!response.ok) {
                setErrors([
                    'Loading products failed. Please try again.',
                ]);
                return;
            }
            const json = await response.json();
            const data: Product[] = (json.data ?? json).map(parseProduct);
            setTotal(json.meta.total ?? data.length);
            setProducts(data);
        } catch (error) {
            console.error(error);
            setErrors([
                'Loading products failed. Please try again.',
            ]);
        } finally {
            setLoading(false);
        }
    }

    return {
        products,
        errors,
        loading,
        page,
        perPage,
        total,
        searchTerm,
        includeInactive,
        supplierId,
        sortField,
        sortOrder,
        fetchProducts,
        setPage,
        setPerPage,
        setSearchTerm,
        setIncludeInactive,
        setSupplierId,
        setSort,
    }
}

export function ProviderProducts({ children }: { children: React.ReactNode }) {
    const products = useProviderProducts();
    return <productsContext.Provider value={products}> {children}</productsContext.Provider>;
}

export const useProducts = () => {
    return useContext(productsContext);
}

export function useProductDetail(id: string | undefined) {
    const [product, setProduct] = useState<Product | null>(null);
    const [stock, setStock] = useState<ProductStock[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const apiService = useApiService();

    useEffect(() => {
        if (id) fetchProduct();
    }, [id]);

    async function fetchProduct() {
        setLoading(true);
        setErrors([]);

        try {
            const [productRes, stockRes] = await Promise.all([
                apiService.get(`/products/${id}`),
                apiService.get(`/products/${id}/stock`),
            ]);

            if (!productRes.ok || !stockRes.ok) {
                setErrors(['Loading product details failed. Please try again.']);
                return;
            }

            const productJson = await productRes.json();
            setProduct(parseProduct(productJson.data ?? productJson));

            const stockJson = await stockRes.json();
            const stockData: ProductStock[] = (stockJson.data ?? stockJson).map((d: any) => ({
                ...d,
                expires_at: new Date(d.expires_at),
            }));
            setStock(stockData);
        } catch (error) {
            console.error(error);
            setErrors(['Loading product details failed. Please try again.']);
        } finally {
            setLoading(false);
        }
    }

    async function saveProduct(updated: Product) {
        setLoading(true);
        setErrors([]);
        setSuccess(false);

        try {
            const response = await apiService.post(`/products/${id}/update`, updated);
        } catch (error) {
            console.error(error);
            setErrors(['Saving product details failed. Please try again.']);
        } finally {
            setLoading(false);
        }
    }

    return { product, stock, errors, loading, fetchProduct, saveProduct, saving, success, setSuccess };
}

export type ExpiryWarningLevels = {
    notice: number | null;
    warning: number | null;
    critical: number | null;
};

export function useProductExpiryWarnings(productId: string | undefined) {
    const [levels, setLevels] = useState<ExpiryWarningLevels>({ notice: null, warning: null, critical: null });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const apiService = useApiService();

    useEffect(() => {
        if (productId) fetchLevels();
    }, [productId]);

    async function fetchLevels() {
        if (!productId) return;
        setLoading(true);
        setError(null);

        try {
            const response = await apiService.get(`/products/${productId}/warnings/expiry`);
            if (response.ok) {
                const json = await response.json();
                const data = json.data ?? json;
                setLevels({
                    notice: data.notice ?? null,
                    warning: data.warning ?? null,
                    critical: data.critical ?? null,
                });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    async function saveLevels(updated: ExpiryWarningLevels) {
        if (!productId) return;
        setSaving(true);
        setError(null);
        setSuccess(false);

        try {
            const response = await apiService.post(`/products/${productId}/warnings/expiry`, updated);
            if (!response.ok) {
                setError("Failed to save warning levels. Please try again.");
                return;
            }
            const json = await response.json();
            const data = json.data ?? json;
            setLevels({
                notice: data.notice ?? updated.notice,
                warning: data.warning ?? updated.warning,
                critical: data.critical ?? updated.critical,
            });
            setSuccess(true);
        } catch (err) {
            console.error(err);
            setError("Failed to save warning levels. Please try again.");
        } finally {
            setSaving(false);
        }
    }

    return { levels, loading, saving, error, success, setSuccess, saveLevels };
}